import type { PaymentEventDef, PaymentEventTier } from '@rb/shared/master-data/types';
import { validateMailReward } from '@rb/shared/mail/reward-validation';
import {
  DELIVERY_ASSET_MAP,
  MAIL_ONLY_DELIVERY_ASSET_MAP,
  STATE_DELIVERY_MAP,
} from '@rb/shared/user-state/types';
import type { RewardRow } from '@/components/admin/mail/serialize';

export const DAY_MS = 86_400_000;
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

export type PaymentEventStatus = 'scheduled' | 'running' | 'grace' | 'ended';

/** 폼 편집용 티어. 보상은 MailRewardPicker 가 쓰는 행 배열로 보관한다. */
export interface TierDraft {
  threshold_vx: number;
  rewards: RewardRow[];
  /**
   * DB 원본 `tiers` 배열에서의 인덱스. 신규 추가 티어는 undefined.
   *
   * 서버는 티어를 배열 index 로만 식별하고 클레임 이력도 raw index 로 저장하므로
   * (`claimedByEvent[eventId] = [tierIndex, ...]`), 진행중 이벤트에서 index 가
   * 밀리면 이미 받은 보상을 한 번 더 받게 된다. `tierIndexShiftIssue` 가 이 값으로
   * "저장 후 위치 == 원래 위치" 를 검사한다.
   */
  originIndex?: number;
}

/**
 * 폼 편집용 이벤트. 기간은 KST wall clock 문자열('YYYY-MM-DDTHH:mm')로 다룬다.
 *
 * `active` 는 일부러 없다 — 폼에는 active 컨트롤이 없는데 목록 라디오는 폼이 열려
 * 있어도 동작하므로, draft 에 스냅샷을 담아두면 저장 시 낡은 값을 write-back 해
 * "active 2개"(또는 0개)를 만든다. 활성 전환은 `setActivePaymentEvent` 전담이다.
 */
export interface PaymentEventDraft {
  id: number;
  startLocal: string;
  endLocal: string;
  grace_days: number;
  title_key: string; // '' = 미설정(PAYMENT_EVENT_TITLE 폴백)
  tiers: TierDraft[];
}

/** EF crud update 에 넘길 행 값 — `active` 는 의도적으로 제외한다(위 주석 참조). */
export interface PaymentEventRowValues {
  id: number;
  start_at: number;
  end_at: number;
  grace_days: number;
  title_key: string | null;
  tiers: PaymentEventTier[];
}

/** EF crud insert 에 넘길 행 값. 신규 행은 항상 비활성으로 만든다. */
export interface PaymentEventInsertValues extends PaymentEventRowValues {
  active: false;
}

export interface ValidationIssue {
  field: string;
  message: string;
}

export function claimDeadline(ev: { end_at: number; grace_days: number }): number {
  return ev.end_at + ev.grace_days * DAY_MS;
}

export function paymentEventStatus(
  ev: { start_at: number; end_at: number; grace_days: number },
  now: number
): PaymentEventStatus {
  if (now < ev.start_at) return 'scheduled';
  if (now <= ev.end_at) return 'running';
  if (now <= claimDeadline(ev)) return 'grace';
  return 'ended';
}

/**
 * 'YYYY-MM-DDTHH:mm' (KST 벽시계) → epoch ms.
 *
 * 로컬 타임존에 의존하는 `new Date(str)` 대신 +09:00 오프셋을 직접 뺀다. 또
 * `Date.UTC` 는 2026-02-29 같은 값을 3/1 로 조용히 롤오버하므로, 역변환이
 * 입력과 같은지 확인해 존재하지 않는 날짜를 거른다.
 */
export function kstLocalToEpochMs(local: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const ms = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi)) - KST_OFFSET_MS;
  if (!Number.isFinite(ms)) return null;
  return epochMsToKstLocal(ms) === local ? ms : null;
}

export function epochMsToKstLocal(ms: number): string {
  const d = new Date(ms + KST_OFFSET_MS);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/** 표시용 — '2026-08-20 00:00 KST'. */
export function formatKst(ms: number): string {
  return `${epochMsToKstLocal(ms).replace('T', ' ')} KST`;
}

export function sortTiers(tiers: PaymentEventTier[]): PaymentEventTier[] {
  return [...tiers].sort((a, b) => a.threshold_vx - b.threshold_vx);
}

/** `sortTiers` 의 draft 판 — `originIndex` 를 유지한 채 저장 시 순서를 미리 본다. */
export function sortTierDrafts(tiers: TierDraft[]): TierDraft[] {
  return [...tiers].sort((a, b) => a.threshold_vx - b.threshold_vx);
}

export function nextEventId(rows: PaymentEventDef[]): number {
  return rows.reduce((max, r) => Math.max(max, r.id), 0) + 1;
}

export type RewardKeyCheck = { ok: true; key: string } | { ok: false; message: string };

/**
 * 보상 키가 `deliverItems` 가 실제로 해석하는 토큰인지 검사하고 정규화한다.
 *
 * 1차는 메일 경로와 같은 권위 검증기 `validateMailReward` 다(키 공간 = deliverItems
 * 토큰). 한때 그쪽 통화 화이트리스트 `MAIL_CURRENCY_KEYS` 가 `DELIVERY_ASSET_MAP` 의
 * 진부분집합이라(BREAKTHROUGH_STONE_*, UPGRADE_RESET_TICKET, QUICK_GAIN_TICKET 누락)
 * 아래 delivery 맵 조회가 그 구멍을 메웠는데, 그 목록이 메일 지급을 막는 사고를 내면서
 * 누락분이 전부 채워졌다(2026-09 핫픽스). 그래도 delivery 맵 폴백은 남긴다 — 이 검증의
 * 기준은 "메일로 줄 수 있는가" 가 아니라 "`deliverItems` 가 해석하는가" 이고, 두 목록이
 * 다시 갈라져도 결제 이벤트가 fail-closed 로 무너지지 않아야 한다(아래 마지막 문단).
 *
 * 반대로 `MAIL_ONLY_DELIVERY_ASSET_MAP` 키(GLORY)는 `validateMailReward` 를 통과해도
 * 여기서 먼저 거부한다. `deliverItems` 가 그 맵을 보려면 호출부가
 * `{ allowMailOnlyAssets: true }` 로 옵트인해야 하는데 옵트인은 `userMail.claimMail`
 * 한 곳뿐이고, 결제 이벤트 클레임은 옵트인 없이 `deliverItems` 를 부른다.
 *
 * 여기서 걸러지지 못한 키는 클레임 시 `deliverItems` 가 어느 분기에도 못 맞춰
 * warn 도 throw 도 없이 빠져나가고, 핸들러는 성공으로 보고 claimed 에 tierIndex 를
 * 기록한다 → 멱등 가드 때문에 재클레임 불가 → 유저 보상 영구 0. 그래서 fail-closed 다.
 */
export function checkRewardKey(rawKey: string): RewardKeyCheck {
  const key = (rawKey ?? '').trim().toUpperCase();
  if (!key) return { ok: false, message: '보상 키를 선택하거나 입력하세요' };

  if (Object.hasOwn(MAIL_ONLY_DELIVERY_ASSET_MAP, key)) {
    return {
      ok: false,
      message: `보상 키 '${key}' 는 메일 전용 재화입니다 — 결제 이벤트 클레임에서는 지급되지 않습니다`,
    };
  }

  const mail = validateMailReward(key, 1);
  if (mail.ok) return { ok: true, key: mail.key };

  if (DELIVERY_ASSET_MAP[key] || STATE_DELIVERY_MAP[key]) return { ok: true, key };

  return {
    ok: false,
    message: `보상 키 '${key}' 는 지급(deliverItems) 키 공간에 없습니다 — 지급되지 않고 클레임만 소진됩니다`,
  };
}

export function rewardRowsFromRecord(rec: Record<string, number>): RewardRow[] {
  return Object.entries(rec ?? {}).map(([key, amount]) => ({ key, amount }));
}

/** 유효한 키만 정규화(trim + 대문자)해 담는다. 무효 키는 `validatePaymentEvent` 가 먼저 막는다. */
export function rewardRecordFromRows(rows: RewardRow[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) {
    const checked = checkRewardKey(r.key);
    if (!checked.ok) continue;
    out[checked.key] = r.amount;
  }
  return out;
}

/**
 * DB 행 → 폼 draft.
 *
 * 티어는 **정렬하지 않고 DB 순서 그대로** 담는다. 클레임 이력이 이 배열의 index 를
 * 가리키므로, 화면 표시를 위해 순서를 바꾸면 `originIndex` 기준 안정성 검사가
 * "편집하지 않았는데도 순서가 바뀐" 것으로 오탐한다. 오름차순 정렬은 저장 시점
 * (`draftToValues`)에만 한다.
 */
export function draftFromRow(row: PaymentEventDef): PaymentEventDraft {
  return {
    id: row.id,
    startLocal: epochMsToKstLocal(row.start_at),
    endLocal: epochMsToKstLocal(row.end_at),
    grace_days: row.grace_days,
    title_key: row.title_key ?? '',
    tiers: (row.tiers ?? []).map((t, i) => ({
      threshold_vx: t.threshold_vx,
      rewards: rewardRowsFromRecord(t.rewards),
      originIndex: i,
    })),
  };
}

/** 복제용 draft — 원본 행을 베끼되 신규 id 를 달고 티어의 원본 index 를 끊는다. */
export function cloneDraftFromRow(row: PaymentEventDef, id: number): PaymentEventDraft {
  const base = draftFromRow(row);
  return {
    ...base,
    id,
    tiers: base.tiers.map((t) => ({ threshold_vx: t.threshold_vx, rewards: t.rewards })),
  };
}

/** 신규 이벤트 기본값 — 내일 00:00 KST 부터 7일, 유예 3일. */
export function emptyDraft(id: number, now: number): PaymentEventDraft {
  const startLocal = `${epochMsToKstLocal(now + DAY_MS).slice(0, 10)}T00:00`;
  const start = kstLocalToEpochMs(startLocal) ?? now;
  return {
    id,
    startLocal,
    endLocal: epochMsToKstLocal(start + 7 * DAY_MS),
    grace_days: 3,
    title_key: '',
    tiers: [{ threshold_vx: 0, rewards: [] }],
  };
}

export function draftToValues(draft: PaymentEventDraft): PaymentEventRowValues {
  return {
    id: draft.id,
    start_at: kstLocalToEpochMs(draft.startLocal) ?? 0,
    end_at: kstLocalToEpochMs(draft.endLocal) ?? 0,
    grace_days: draft.grace_days,
    title_key: draft.title_key.trim() || null,
    tiers: sortTiers(
      draft.tiers.map((t) => ({
        threshold_vx: t.threshold_vx,
        rewards: rewardRecordFromRows(t.rewards),
      }))
    ),
  };
}

/** 신규 행은 항상 비활성으로 만든다 — 활성 전환은 목록 라디오(setActivePaymentEvent) 전담. */
export function draftToInsertValues(draft: PaymentEventDraft): PaymentEventInsertValues {
  return { ...draftToValues(draft), active: false };
}

/**
 * 이미 시작된 이벤트에서 티어 index 가 밀리는 편집을 잡아낸다.
 *
 * 서버는 티어를 배열 index 로만 다루고 클레임 이력도 raw index 로 저장한다
 * (`ev.tiers[tierIndex]`, `claimed=[...claimed, tierIndex]`). 예: [3000, 6000] 운영 중
 * 유저가 index 0 을 클레임한 뒤 운영자가 1000 티어를 추가하면 저장 시 [1000, 3000, 6000]
 * 으로 정렬돼 claimed=[0] 이 1000 을 가리키게 되고 3000 은 미클레임이 되어 **한 번 더**
 * 지급된다.
 *
 * 그래서 `scheduled` 가 아닌 이벤트에서는 기존 티어가 저장 후에도 원래 index 를
 * 그대로 유지하는 편집만 허용한다. 즉 (1) 티어 삭제 금지, (2) 신규 티어는 정렬 후
 * 맨 뒤에만 붙을 수 있음(= 기존 최대 임계값보다 커야 함), (3) 임계값·보상 수정은
 * 정렬 순서를 바꾸지 않는 범위에서 허용.
 *
 * 경고 후 진행이 아니라 **차단**을 택했다: 중복 지급은 되돌릴 수 없는 재화 유출이고,
 * 확인 다이얼로그는 반복 조작 중 습관적으로 눌리기 쉽다. 정말 구조를 바꿔야 하면
 * 복제(신규 이벤트)라는 안전한 우회로가 이미 패널에 있다.
 */
export function tierIndexShiftIssue(
  draft: PaymentEventDraft,
  original: PaymentEventDef,
  now: number
): ValidationIssue | null {
  if (paymentEventStatus(original, now) === 'scheduled') return null;

  const originalCount = (original.tiers ?? []).length;
  const sorted = sortTierDrafts(draft.tiers);
  const stable =
    sorted.length >= originalCount &&
    sorted.every((t, i) => (i < originalCount ? t.originIndex === i : t.originIndex === undefined));
  if (stable) return null;

  return {
    field: 'tiers',
    message:
      '이미 시작된 이벤트는 티어의 개수·순서를 바꿀 수 없습니다 — 클레임 이력이 배열 index 로 저장돼 이미 받은 티어를 다시 받게 됩니다. 임계값·보상만 (정렬 순서가 유지되는 범위에서) 수정하거나, 복제해 새 이벤트로 만드세요.',
  };
}

export interface ValidateOptions {
  /** 편집 대상의 DB 원본. 신규(insert) 경로에서는 넘기지 않는다. */
  original?: PaymentEventDef;
  /** 상태 판정 기준 시각. 기본값 `Date.now()`. */
  now?: number;
}

export function validatePaymentEvent(
  draft: PaymentEventDraft,
  others: PaymentEventDef[],
  options: ValidateOptions = {}
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const start = kstLocalToEpochMs(draft.startLocal);
  const end = kstLocalToEpochMs(draft.endLocal);

  if (start === null) issues.push({ field: 'start', message: '시작 일시를 입력하세요' });
  // end_at=0 은 코드상 '무기한' 의도지만 claimPaymentEventTier 의 마감 컷이 0 을
  // 그대로 비교해 즉시 마감이 된다. 명시적 미래 시각만 허용한다.
  if (end === null) issues.push({ field: 'end', message: '종료 일시를 입력하세요' });
  if (start !== null && end !== null && start >= end) {
    issues.push({ field: 'end', message: '종료가 시작보다 뒤여야 합니다' });
  }

  if (!Number.isInteger(draft.grace_days) || draft.grace_days < 0) {
    issues.push({ field: 'grace', message: '유예일은 0 이상 정수여야 합니다' });
  }

  if (others.some((o) => o.id === draft.id)) {
    issues.push({ field: 'id', message: `id ${draft.id} 는 이미 존재합니다` });
  }

  if (draft.tiers.length === 0) {
    issues.push({ field: 'tiers', message: '티어를 최소 1개 추가하세요' });
  }

  if (options.original) {
    const shift = tierIndexShiftIssue(draft, options.original, options.now ?? Date.now());
    if (shift) issues.push(shift);
  }

  const seenThreshold = new Set<number>();
  draft.tiers.forEach((tier, i) => {
    if (!Number.isInteger(tier.threshold_vx) || tier.threshold_vx <= 0) {
      issues.push({ field: `tier.${i}.threshold`, message: 'VX 임계값은 1 이상 정수여야 합니다' });
    } else if (seenThreshold.has(tier.threshold_vx)) {
      issues.push({ field: `tier.${i}.threshold`, message: 'VX 임계값이 중복됩니다' });
    } else {
      seenThreshold.add(tier.threshold_vx);
    }

    if (tier.rewards.length === 0) {
      issues.push({ field: `tier.${i}.rewards`, message: '보상을 최소 1개 추가하세요' });
    }

    const seenKey = new Set<string>();
    tier.rewards.forEach((r, j) => {
      const field = `tier.${i}.reward.${j}`;
      // 키 공간 검증 — 여기서 놓치면 클레임이 조용히 소진되고 보상은 0 이 된다.
      const checked = checkRewardKey(r.key);
      if (!checked.ok) {
        issues.push({ field, message: checked.message });
        return;
      }
      // 수량 0 이하는 deliverItems 가 조용히 skip 하므로 저장 자체를 막는다.
      if (!Number.isInteger(r.amount) || r.amount < 1) {
        issues.push({ field, message: '수량은 1 이상 정수여야 합니다' });
      }
      // 정규화된 키로 비교한다 — 공백·대소문자 차이로 중복 검사가 우회되면
      // rewardRecordFromRows 에서 한쪽이 조용히 덮어써진다.
      if (seenKey.has(checked.key)) {
        issues.push({ field, message: `보상 키 ${checked.key} 가 중복됩니다` });
      }
      seenKey.add(checked.key);
    });
  });

  return issues;
}
